/**
 * RiskPanel — four horizontal bars showing how close the user is to
 * each of their per-account risk limits today.
 *
 * Daily PnL bar is special: it fills DOWNWARD from the limit (i.e.
 * −5% / −5% = 100% full, red). Trades / positions / consec losses
 * are simple usage/limit ratios.
 */
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/cn'
import { formatPct } from '@/lib/format'
import type { RiskUsage } from '@/hooks/useDashboardApi'

interface RiskPanelProps {
  data: RiskUsage | undefined
  loading?: boolean
}

export function RiskPanel({ data, loading }: RiskPanelProps) {
  const { t } = useTranslation('app')

  return (
    <section className="bg-ink p-5">
      <header className="flex items-baseline mb-4">
        <h3 className="text-sm font-semibold">{t('dashboard.risk.title')}</h3>
      </header>

      {loading || !data ? (
        <div className="text-textFaint text-sm">—</div>
      ) : (
        <div className="space-y-4 text-sm">
          <PnlBar value={data.daily_pnl_pct} limit={data.daily_loss_limit_pct} />
          <UsageBar
            label={t('dashboard.risk.trades')}
            value={data.trades_today}
            limit={null}
          />
          <UsageBar
            label={t('dashboard.risk.positions')}
            value={data.open_positions}
            limit={data.max_concurrent_positions}
          />
          <UsageBar
            label={t('dashboard.risk.consec_losses')}
            value={data.consecutive_losses}
            limit={null}
            tone={
              data.consecutive_losses >= 3 ? 'rose'
              : data.consecutive_losses >= 2 ? 'amber'
              : 'pulse'
            }
          />
        </div>
      )}
    </section>
  )
}

function PnlBar({ value, limit }: { value: number; limit: number }) {
  const { t } = useTranslation('app')
  // limit is positive, value is signed. Fill ratio = how far into the
  // loss budget we are; 0 if positive, 100% if at -limit.
  const ratio = limit > 0 && value < 0
    ? Math.min(1, Math.abs(value) / limit)
    : 0
  const tone = ratio >= 0.8 ? 'rose' : ratio >= 0.5 ? 'amber' : 'pulse'
  return (
    <div>
      <div className="flex items-baseline mb-1.5">
        <span className="text-textDim">{t('dashboard.risk.daily_pnl')}</span>
        <span className={cn(
          'ml-auto font-mono',
          value >= 0 ? 'text-pulse' : 'text-rose',
        )}>
          {formatPct(value, { sign: true })}
        </span>
      </div>
      <Bar ratio={ratio} tone={tone} />
      <div className="text-xs text-textFaint mt-1">
        {t('dashboard.risk.limit_pct', { pct: limit.toFixed(1) })}
      </div>
    </div>
  )
}

function UsageBar({
  label, value, limit, tone,
}: {
  label: string
  value: number
  limit: number | null
  tone?: 'pulse' | 'amber' | 'rose'
}) {
  const { t } = useTranslation('app')
  const ratio = limit && limit > 0 ? Math.min(1, value / limit) : 0
  const autoTone = tone ?? (
    ratio >= 0.9 ? 'rose' : ratio >= 0.66 ? 'amber' : 'pulse'
  )
  return (
    <div>
      <div className="flex items-baseline mb-1.5">
        <span className="text-textDim">{label}</span>
        <span className="ml-auto font-mono">
          {value}
          {limit != null && (
            <span className="text-textFaint ml-1">
              {t('dashboard.risk.of', { limit })}
            </span>
          )}
        </span>
      </div>
      {limit != null && <Bar ratio={ratio} tone={autoTone} />}
    </div>
  )
}

function Bar({
  ratio, tone,
}: { ratio: number; tone: 'pulse' | 'amber' | 'rose' }) {
  const toneClass = {
    pulse: 'bg-pulse',
    amber: 'bg-amber',
    rose:  'bg-rose',
  }[tone]
  return (
    <div className="h-1 bg-elevated rounded overflow-hidden">
      <div
        className={cn('h-full transition-all duration-300', toneClass)}
        style={{ width: `${Math.max(0, Math.min(100, ratio * 100))}%` }}
      />
    </div>
  )
}
